import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';

import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import VideoCard from '../components/VideoCard';
import videos, { findVideoBySlug, videoPath } from '../data/videos';
import '../styles/pages/work.css';

/**
 * Permanent page for a single walkthrough, at /demos/video/<slug>.
 * This is the URL to share: it's ours, and it stays valid if the underlying
 * YouTube upload is ever replaced.
 *
 * Keyed by slug: moving between two video pages remounts the page, so the
 * split-text hero headline is rebuilt from fresh React-owned DOM instead of
 * React patching text nodes the motion layer has already split.
 */
function VideoDetail() {
  const { slug } = useParams();
  return <VideoDetailPage key={slug} slug={slug} />;
}

function VideoDetailPage({ slug }) {
  const video = findVideoBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!video) return;

    document.title = `${video.title} - Moneli Automation Inc.`;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = video.paragraphs[0];

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.paragraphs.join(' '),
      thumbnailUrl: `https://i.ytimg.com/vi/${video.youtubeId}/maxresdefault.jpg`,
      embedUrl: `https://www.youtube-nocookie.com/embed/${video.youtubeId}`,
      url: `https://moneliautomation.com${videoPath(video)}`,
      publisher: {
        '@type': 'Organization',
        name: 'Moneli Automation Inc.',
        logo: 'https://moneliautomation.com/logo.png'
      }
    };

    let schemaScript = document.getElementById('video-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'video-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(structuredData);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: video.title,
        page_location: window.location.href,
        page_path: videoPath(video)
      });
    }

    return () => {
      document.title = 'Moneli Automation Inc.';
    };
  }, [video]);

  // Unknown slug: send visitors to the demos index rather than a dead end.
  if (!video) return <Navigate to="/demos" replace />;

  const trackVideoPlay = (played, startSeconds) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'video_play', {
        video_title: played.title,
        video_id: played.youtubeId,
        start_seconds: startSeconds || 0
      });
    }
  };

  // "Keep watching": the next two walkthroughs in the list order (wrapping),
  // which is the order the data file says a first-time visitor should watch.
  const at = videos.findIndex((v) => v.slug === video.slug);
  const next = [1, 2]
    .map((step) => videos[(at + step) % videos.length])
    .filter((v, i, arr) => v.slug !== video.slug && arr.findIndex((o) => o.slug === v.slug) === i);

  return (
    <div className="work-page work-detail">
      <PageHero
        variant="dark"
        className="work-detail__hero"
        eyebrow={<>Video walkthrough{video.duration ? <> <span aria-hidden="true">·</span> {video.duration}</> : null}</>}
        title={video.title}
        crumbs={[{ to: '/', label: 'Home' }, { to: '/demos', label: 'Demos' }, { label: 'Video' }]}
        actions={
          <Link className="btn btn-outline-light" to="/demos">
            <i className="fa-solid fa-arrow-left" aria-hidden="true"></i> All demos
          </Link>
        }
      />

      <section className="section section--light section--flush-top work-detail__player" aria-label="Video">
        <div className="container">
          {/* The page hero carries the h1 (this page is in the sitemap and needs a
              top-level heading), so the card drops its own title. */}
          <div data-reveal>
            <VideoCard
              video={video}
              onPlay={trackVideoPlay}
              showPermalink={false}
              showDetails
              showTitle={false}
            />
          </div>
        </div>
      </section>

      {next.length > 0 && (
        <section className="section section--sand" aria-labelledby="keep-watching-title">
          <div className="container">
            <SectionHeader
              eyebrow="Up next"
              title={<>Keep <em>watching</em></>}
              lead="More short walkthroughs of the real product."
              id="keep-watching-title"
            />
            <div className="work-videos">
              {next.map((v, i) => (
                <div className="work-videos__item" key={v.slug} data-reveal data-delay={String(i * 0.08)}>
                  <VideoCard video={v} onPlay={trackVideoPlay} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default VideoDetail;
